import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  ClipboardList, Send, CheckCircle, School, Calendar, User, Volume2, VolumeX, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Opzioni domande
const Q1_OPTIONS = [
  { value: 'reti_plastica', label: 'Reti di plastica' },
  { value: 'bottiglie_tappi', label: 'Bottiglie/Tappi' },
  { value: 'microplastiche', label: 'Microplastiche (i pezzetti piccoli)' }
];

const Q3_OPTIONS = [
  { value: 'si_sapevo', label: 'Sì, lo sapevo.' },
  { value: 'no_scoperto', label: 'No, l\'ho scoperto con il laboratorio.' }
];

const Q4_OPTIONS = [
  { value: 'si_fatto_caso', label: 'Sì, ci ho fatto caso.' },
  { value: 'non_ancora', label: 'Non ancora, ma inizierò oggi.' }
];

const Q5_OPTIONS = [
  { value: 'meno_bottiglie', label: 'Ho usato meno bottiglie di plastica (es. usato la borraccia).' },
  { value: 'spiegato_genitori', label: 'Ho spiegato ai miei genitori cos\'è una rete fantasma.' },
  { value: 'raccolto_rifiuto', label: 'Ho raccolto un rifiuto di plastica che ho visto per strada o in spiaggia.' },
  { value: 'riutilizzare', label: 'Ho pensato a come riutilizzare un oggetto di plastica invece di buttarlo.' }
];

const Q6_OPTIONS = [
  { value: 'si_so_cercare', label: 'Sì, ora so cosa cercare.' },
  { value: 'non_sicuro', label: 'Non sono sicuro, mi serve un altro suggerimento.' }
];

const QuestionnaireForm = ({ onClose, onSubmitted }) => {
  const { language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const audioElementRef = useRef(null);
  
  // Emme Guide per il questionario
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('questionario');
  
  const [formData, setFormData] = useState({
    lab_date: '',
    school_name: '',
    class_name: '',
    student_name: '',
    q1_materiale_pericoloso: '',
    q2_storia_rafa: '',
    q3_reti_fantasma: '',
    q4_plastica_famiglia: '',
    q5_azioni: [],
    q6_riconoscere_rifiuto: '',
    q7_inquinamento: '',
    q8_piaciuto_piu: ''
  });

  // Funzione per leggere le domande con TTS (voce di Emme - OpenAI Nova)
  const handleReadQuestions = async () => {
    // Se sta già leggendo, ferma
    if (isReading && audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsReading(false);
      return;
    }

    // Se abbiamo già l'audio caricato, riproducilo
    if (audioSrc && audioElementRef.current) {
      try {
        setIsReading(true);
        audioElementRef.current.currentTime = 0;
        await audioElementRef.current.play();
        return;
      } catch (e) {
        console.error('Replay error:', e);
      }
    }

    // Altrimenti carica l'audio
    try {
      setIsReading(true);
      toast.info('Emme sta preparando la lettura...', { duration: 5000 });
      
      const response = await axios.get(`${API_URL}/api/tts/questionnaire`);
      
      if (response.data.audio) {
        const audioUrl = `data:audio/mp3;base64,${response.data.audio}`;
        setAudioSrc(audioUrl);
        
        // Attendi che l'elemento audio sia pronto e riproduci
        setTimeout(async () => {
          if (audioElementRef.current) {
            try {
              await audioElementRef.current.play();
            } catch (playError) {
              console.error('Play error:', playError);
              toast.error('Premi di nuovo per ascoltare');
              setIsReading(false);
            }
          }
        }, 100);
      } else {
        toast.error('Nessun audio ricevuto');
        setIsReading(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Errore nella lettura vocale');
      setIsReading(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    const audioEl = audioElementRef.current;
    return () => {
      if (audioEl) {
        audioEl.pause();
      }
    };
  }, []);

  const handleQ5Change = (value) => {
    const current = formData.q5_azioni;
    if (current.includes(value)) {
      setFormData({ ...formData, q5_azioni: current.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, q5_azioni: [...current, value] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione base
    if (!formData.lab_date || !formData.school_name || !formData.class_name) {
      toast.error('Compila data, scuola e classe');
      return;
    }
    
    if (!formData.q1_materiale_pericoloso || !formData.q3_reti_fantasma || 
        !formData.q4_plastica_famiglia || !formData.q6_riconoscere_rifiuto) {
      toast.error('Rispondi a tutte le domande a scelta multipla');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/questionnaires`, formData);
      setSubmitted(true);
      toast.success('Questionario inviato!');
      if (onSubmitted) onSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Grazie!</h3>
        <p className="text-slate-400 mb-6">Il tuo questionario è stato inviato con successo.</p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
        >
          Chiudi
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Emme Guide */}
      {showGuide && <EmmeGuide sectionId="questionario" onClose={closeGuide} />}
      
      {/* Audio element hidden per TTS */}
      {audioSrc && (
        <audio
          ref={audioElementRef}
          src={audioSrc}
          onEnded={() => setIsReading(false)}
          onError={() => {
            setIsReading(false);
            toast.error('Errore riproduzione audio');
          }}
          preload="auto"
        />
      )}
      
      {/* Pulsante Leggi Domande con Emme */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex-shrink-0">
            <img
              src="/emme/happy.png?v3"
              alt="Emme"
              className="w-full h-full object-contain drop-shadow-lg"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="flex-1">
            <p className="text-cyan-300 text-sm mb-2">
              Vuoi che ti legga tutte le domande?
            </p>
            <button
              type="button"
              onClick={handleReadQuestions}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                isReading 
                  ? 'bg-red-500/80 hover:bg-red-500 text-white' 
                  : 'bg-cyan-500 hover:bg-cyan-600 text-white'
              }`}
            >
              {isReading ? (
                <>
                  <VolumeX className="w-5 h-5" />
                  Ferma lettura
                </>
              ) : (
                <>
                  <Volume2 className="w-5 h-5" />
                  Leggi tutte le domande
                </>
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={openGuide}
            className="p-2 rounded-full bg-slate-700/50 hover:bg-slate-600/50 text-cyan-400 transition-colors"
            title="Aiuto"
          >
            ?
          </button>
        </div>
      </div>

      {/* Header Info */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
        <h3 className="text-emerald-400 font-bold flex items-center gap-2 mb-3">
          <ClipboardList className="w-5 h-5" />
          Questionario MareDu
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 uppercase flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Data Laboratorio *
            </label>
            <input
              type="date"
              value={formData.lab_date}
              onChange={(e) => setFormData({...formData, lab_date: e.target.value})}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              required
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase flex items-center gap-1">
              <School className="w-3 h-3" /> Scuola *
            </label>
            <input
              type="text"
              value={formData.school_name}
              onChange={(e) => setFormData({...formData, school_name: e.target.value})}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              placeholder="Nome scuola"
              required
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase">Classe *</label>
            <input
              type="text"
              value={formData.class_name}
              onChange={(e) => setFormData({...formData, class_name: e.target.value})}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              placeholder="Es: 3A"
              required
            />
          </div>
        </div>
        
        <div className="mt-3">
          <label className="text-xs text-slate-400 uppercase flex items-center gap-1">
            <User className="w-3 h-3" /> Il tuo nome (opzionale)
          </label>
          <input
            type="text"
            value={formData.student_name}
            onChange={(e) => setFormData({...formData, student_name: e.target.value})}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            placeholder="Nome e cognome"
          />
        </div>
      </div>

      {/* Domanda 1 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          1. Quale materiale che abbiamo toccato in classe ti è sembrato più pericoloso per i pesci?
        </p>
        <div className="space-y-2">
          {Q1_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-800">
              <input
                type="radio"
                name="q1"
                value={opt.value}
                checked={formData.q1_materiale_pericoloso === opt.value}
                onChange={(e) => setFormData({...formData, q1_materiale_pericoloso: e.target.value})}
                className="w-4 h-4 text-emerald-500"
              />
              <span className="text-slate-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Domanda 2 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          2. Ti ricordi di Rafa? Qual è la cosa che ti ha colpito di più della sua storia?
        </p>
        <textarea
          value={formData.q2_storia_rafa}
          onChange={(e) => setFormData({...formData, q2_storia_rafa: e.target.value})}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          rows={3}
          placeholder="Scrivi qui la tua risposta..."
        />
      </div>

      {/* Domanda 3 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          3. Prima dell'incontro, sapevi che le "reti fantasma" continuano a pescare e a ferire il mare anche se nessuno le usa più?
        </p>
        <div className="space-y-2">
          {Q3_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-800">
              <input
                type="radio"
                name="q3"
                value={opt.value}
                checked={formData.q3_reti_fantasma === opt.value}
                onChange={(e) => setFormData({...formData, q3_reti_fantasma: e.target.value})}
                className="w-4 h-4 text-emerald-500"
              />
              <span className="text-slate-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Domanda 4 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          4. Negli ultimi giorni, hai fatto attenzione a quanta plastica usa la tua famiglia?
        </p>
        <div className="space-y-2">
          {Q4_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-800">
              <input
                type="radio"
                name="q4"
                value={opt.value}
                checked={formData.q4_plastica_famiglia === opt.value}
                onChange={(e) => setFormData({...formData, q4_plastica_famiglia: e.target.value})}
                className="w-4 h-4 text-emerald-500"
              />
              <span className="text-slate-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Domanda 5 - Checkbox multipli */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          5. Quale di queste azioni hai provato a fare dopo il laboratorio? <span className="text-slate-400 text-sm">(Puoi segnare più risposte)</span>
        </p>
        <div className="space-y-2">
          {Q5_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-800">
              <input
                type="checkbox"
                checked={formData.q5_azioni.includes(opt.value)}
                onChange={() => handleQ5Change(opt.value)}
                className="w-4 h-4 text-emerald-500 rounded"
              />
              <span className="text-slate-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Domanda 6 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          6. Se domani andassi a fare una passeggiata in spiaggia o vicino a un fiume, sapresti riconoscere un rifiuto che viene dal mare?
        </p>
        <div className="space-y-2">
          {Q6_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-800">
              <input
                type="radio"
                name="q6"
                value={opt.value}
                checked={formData.q6_riconoscere_rifiuto === opt.value}
                onChange={(e) => setFormData({...formData, q6_riconoscere_rifiuto: e.target.value})}
                className="w-4 h-4 text-emerald-500"
              />
              <span className="text-slate-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Domanda 7 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          7. Scrivi una frase per spiegare cos'è l'inquinamento:
        </p>
        <textarea
          value={formData.q7_inquinamento}
          onChange={(e) => setFormData({...formData, q7_inquinamento: e.target.value})}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          rows={3}
          placeholder="Scrivi qui la tua risposta..."
        />
      </div>

      {/* Domanda 8 */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
        <p className="text-white font-medium mb-3">
          8. Scrivi cosa ti è piaciuto di più dell'incontro nella tua classe.
        </p>
        <textarea
          value={formData.q8_piaciuto_piu}
          onChange={(e) => setFormData({...formData, q8_piaciuto_piu: e.target.value})}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          rows={3}
          placeholder="Scrivi qui la tua risposta..."
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Send className="w-5 h-5" />
            Invia Questionario
          </>
        )}
      </button>
    </form>
  );
};

export default QuestionnaireForm;
